import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, ArrowLeft, Award, Trophy, ExternalLink } from 'lucide-react';
import api, { teamAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import Timeline from '../../components/ui/Timeline';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { useRegistration } from '../../contexts/RegistrationContext';
import UnregisterDialog from '../../components/ui/UnregisterDialog';
import RsvpControl from '../../components/events/RsvpControl';
import AddToCalendar from '../../components/events/AddToCalendar';
import CheckInPass from '../../components/events/CheckInPass';
import { EventSpeaker } from '../../types/events';

interface Event {
  _id: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location?: string;
  eventType: string;
  maxParticipants?: number;
  currentParticipants?: number;
  registrationRequired: boolean;
  registrationType?: 'mandatory_signup' | 'optional_signup';
  /** 'rsvp' shows one-tap going/maybe/not-going; 'registration' uses the full form. */
  attendanceMode?: 'registration' | 'rsvp';
  capacity?: number;
  currentRegistrations?: number;
  isVirtual?: boolean;
  meetingLink?: string;
  guestFormFields?: Array<{
    fieldName: string;
    fieldLabel: string;
    fieldType: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox' | 'radio';
    required: boolean;
    options?: string[];
    placeholder?: string;
    validation?: 'none' | 'email' | 'phone' | 'url' | 'number';
  }>;
  registrationDeadline?: string;
  isActive: boolean;
  isPaidEvent?: boolean;
  eventFee?: number;
  showParticipantCount?: boolean;
  // Hackathon specific fields
  hackathon?: {
    _id: string;
    maxTeams: number;
    currentTeams: number;
    maxTeamSize: number;
    minTeamSize: number;
    prizes: Array<{ rank: number; description: string; value: string }>;
    problemStatements: Array<{
      title: string;
      description: string;
      category: string;
      difficulty: string;
    }>;
    rules: string[];
    requirements: string[];
    phases?: Array<{
      _id: string;
      name: string;
      description: string;
      startDate: string;
      endDate: string;
      order: number;
      deliverables?: Array<{
        name: string;
        description: string;
        required: boolean;
      }>;
    }>;
  };
  speakers?: EventSpeaker[];
  eventFlyer?: string;
}

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
    const { 
    checkRegistration, 
    getRegistrationStatus, 
    isChecking,
    unregisterFromHackathon,
    unregisterFromEvent
  } = useRegistration();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnregisterDialog, setShowUnregisterDialog] = useState(false);
  const [unregisterLoading, setUnregisterLoading] = useState(false);
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [showAvailableAspirants, setShowAvailableAspirants] = useState(false);
  const [availableAspirants, setAvailableAspirants] = useState<any[]>([]);
  const [aspirantsLoading, setAspirantsLoading] = useState(false);
  const [aspirantsSearchTerm, setAspirantsSearchTerm] = useState('');

  // Don't render buttons until user context is loaded
  const isUserLoaded = !authLoading;

  useEffect(() => {
    if (id) {
      fetchEvent();
    }
  }, [id]);

  // Check registration status for events and hackathons
  useEffect(() => {
    if (id && isUserLoaded && user && event && !isChecking(event._id)) {
      if (event.eventType === 'hackathon' && event.hackathon?._id) {
        console.log('🔍 EventDetail: Checking registration for hackathon:', event.hackathon._id);
        checkRegistration(event.hackathon._id, 'hackathon');
      } else if (event.eventType !== 'hackathon') {
        console.log('🔍 EventDetail: Checking registration for event:', event._id);
        checkRegistration(event._id, 'event');
      }
    }
  }, [id, isUserLoaded, user, event, checkRegistration, isChecking]);

  // Whoever can take part needs their teams loaded, administrators included —
  // otherwise the team status below never appears for them.
  const takesPart = ['aspirant', 'mentor', 'admin'].includes(user?.role || '');

  useEffect(() => {
    if (isUserLoaded && takesPart) {
      fetchUserTeams();
    }
  }, [isUserLoaded, user]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/events/${id}`);
      setEvent(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch event details');
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTeams = async () => {
    try {
      setTeamsLoading(true);
      const response = await teamAPI.getMyTeams();
      setUserTeams(response);
    } catch (err: any) {
      console.error('Error fetching user teams:', err);
      setUserTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const getUserTeamForHackathon = (hackathonId: string) => {
    return userTeams.find(team => team.hackathon?._id === hackathonId);
  };

  const getUserRoleInTeam = (team: any) => {
    if (!team || !user) return null;
    
    // Check if user is leader
    if (team.leader?._id === user._id) {
      return 'leader';
    }
    
    // Check if user is member
    const isMember = team.members?.some((member: any) => member.user?._id === user._id);
    if (isMember) {
      return 'member';
    }
    
    return null;
  };

  const handleRegister = () => {
    if (!event) return;
    // Navigate to unified registration page
    navigate(`/events/${event._id}/register`);
  };

  const handleUnregister = async (leaveTeam: boolean) => {
    if (!event) return;
    
    try {
      setUnregisterLoading(true);
      
      if (event.eventType === 'hackathon' && event.hackathon?._id) {
        // Handle hackathon unregistration
        await unregisterFromHackathon(event.hackathon._id, leaveTeam);
      } else {
        // Handle regular event unregistration
        await unregisterFromEvent(event._id);
      }
      
      setShowUnregisterDialog(false);
      // The registration status will be automatically cleared by the context
    } catch (error) {
      console.error('Failed to unregister:', error);
    } finally {
      setUnregisterLoading(false);
    }
  };

  const handleDownloadReceipt = async (paymentId: string) => {
    try {
      const response = await fetch(`/api/payments/receipt/${paymentId}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${paymentId}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download receipt');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
    }
  };

  const fetchAvailableAspirants = async () => {
    try {
      setAspirantsLoading(true);
      const response = await fetch(`/api/hackathons/${event?.hackathon?._id}/available-aspirants`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableAspirants(data.aspirants || []);
      } else {
        console.error('Failed to fetch available aspirants');
      }
    } catch (error) {
      console.error('Error fetching available aspirants:', error);
    } finally {
      setAspirantsLoading(false);
    }
  };

  const filteredAspirants = availableAspirants.filter(aspirant => {
    const searchTerm = aspirantsSearchTerm.toLowerCase();
    return (
      aspirant.firstName?.toLowerCase().includes(searchTerm) ||
      aspirant.lastName?.toLowerCase().includes(searchTerm) ||
      aspirant.email?.toLowerCase().includes(searchTerm) ||
      aspirant.phone?.toLowerCase().includes(searchTerm) ||
      aspirant.address?.toLowerCase().includes(searchTerm) ||
      aspirant.primarySkills?.some((skill: string) => skill.toLowerCase().includes(searchTerm)) ||
      aspirant.secondarySkills?.some((skill: string) => skill.toLowerCase().includes(searchTerm))
    );
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Registration Closed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days} days left`;
    if (hours > 0) return `${hours} hours left`;
    return `${minutes} minutes left`;
  };

  const getAspirantExperience = (aspirant: any) => {
    try {
      // Debug: Log the aspirant data to see what we're working with
      console.log('🔍 Aspirant experience data:', {
        name: `${aspirant.firstName} ${aspirant.lastName}`,
        experience: aspirant.experience,
        professionalInfo: aspirant.professionalInfo,
        education: aspirant.education
      });
      
      // Handle experience array (new format)
      if (aspirant.experience && Array.isArray(aspirant.experience) && aspirant.experience.length > 0) {
        const totalYears = aspirant.experience.reduce((total: number, exp: any) => {
          const startDate = new Date(exp.startDate);
          const endDate = exp.endDate ? new Date(exp.endDate) : new Date();
          const years = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
          return total + years;
        }, 0);
        return `${Math.round(totalYears)} years`;
      }
      
      // Handle professionalInfo (legacy format)
      if (aspirant.professionalInfo?.yearsOfExperience) {
        return `${aspirant.professionalInfo.yearsOfExperience} years`;
      }
      
      // Handle education-based calculation
      if (aspirant.education?.graduationYear) {
        const currentYear = new Date().getFullYear();
        const yearsSinceGraduation = currentYear - aspirant.education.graduationYear;
        if (yearsSinceGraduation >= 0) {
          return yearsSinceGraduation === 0 ? 'Student' : `${yearsSinceGraduation} years`;
        }
      }
      
      return 'Student';
    } catch (error) {
      console.error('Error calculating experience for aspirant:', aspirant, error);
      return 'Student';
    }
  };

  const getEventStatus = (event: Event) => {
    const now = new Date();
    const startDate = new Date(event.startTime);
    const endDate = new Date(event.endTime);
    
    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'ongoing';
    return 'past';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show loading spinner if auth is still loading
  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error || 'Event not found'}</div>
          <Link to="/events">
            <Button variant="outline">Back to Events</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isHackathon = event.eventType === 'hackathon';
  // Typed as a hackathon *and* carrying a hackathon record, so teams, problem
  // statements and phases exist. Without the record it behaves as a plain event.
  const isTeamHackathon = isHackathon && !!event.hackathon;

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{event.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="text-lg text-orange-800">Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 mr-2 text-orange-600" />
                  <span className="text-orange-700">Start: {formatDate(event.startTime)} at {formatTime(event.startTime)}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 mr-2 text-orange-600" />
                  <span className="text-orange-700">End: {formatDate(event.endTime)} at {formatTime(event.endTime)}</span>
                </div>
                {event.location && (
                  <div className="flex items-center text-sm">
                    <MapPin className="h-4 w-4 mr-2 text-orange-600" />
                    <span className="text-orange-700">{event.location}</span>
                  </div>
                )}
                {event.registrationDeadline && (
                  <div className="flex items-center text-sm">
                    <Clock className="h-4 w-4 mr-2 text-orange-600" />
                    <span className="text-orange-700">Registration Deadline: {formatDate(event.registrationDeadline)} at {formatTime(event.registrationDeadline)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Participation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isHackathon && event.hackathon ? (
                  <>
                    <div className="flex items-center text-sm">
                      <Users className="h-4 w-4 mr-2 text-gray-500" />
                      <span>{event.hackathon.currentTeams}/{event.hackathon.maxTeams} teams</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span>Team Size: {event.hackathon.minTeamSize}-{event.hackathon.maxTeamSize} members</span>
                    </div>
                  </>
                ) : (
                  <>
                    {event.showParticipantCount !== false || user?.role === 'admin' ? (
                      <div className="flex items-center text-sm">
                        <Users className="h-4 w-4 mr-2 text-gray-500" />
                        <span>
                          {/* RSVP events count responses, not the separate participant tally. */}
                          {event.attendanceMode === 'rsvp'
                            ? `${event.currentRegistrations || 0}${event.capacity ? ` / ${event.capacity}` : ''} attending`
                            : `${event.currentParticipants || 0}${event.maxParticipants ? ` / ${event.maxParticipants}` : ''} participants`}
                        </span>
                      </div>
                    ) : null}
                  </>
                )}
                <div className="flex items-center text-sm">
                  <StatusBadge status={getEventStatus(event)} />
                </div>
                {event.registrationDeadline && (
                  <div className="flex items-center text-sm">
                    <Clock className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{getTimeRemaining(event.registrationDeadline)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Event Speakers */}
          {event.speakers && event.speakers.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Event Speakers</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {event.speakers.map((speaker: any, index: number) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex items-start space-x-3">
                      <div className="relative">
                        {speaker.picture ? (
                          <img 
                            src={speaker.picture} 
                            alt={speaker.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                            onError={async (e) => {
                              const target = e.target as HTMLImageElement;
                              
                              // Try to get signed URL as fallback for private images
                              try {
                                const key = speaker.picture.split('/').pop();
                                if (key) {
                                  const response = await fetch(`/api/upload/speaker-picture-url/${key}`);
                                  if (response.ok) {
                                    const data = await response.json();
                                    if (data.success && data.url) {
                                      target.src = data.url;
                                      return;
                                    }
                                  }
                                }
                              } catch (error) {
                                console.error('Failed to get signed URL for speaker image:', error);
                              }
                              
                              // If signed URL also fails, show initials instead of hiding
                              target.style.display = 'none';
                              const initialsContainer = target.parentElement?.querySelector('.speaker-initials');
                              if (initialsContainer) {
                                (initialsContainer as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300 speaker-initials ${speaker.picture ? 'hidden' : ''}`}>
                          <span className="text-gray-500 text-xs font-medium">
                            {speaker.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">
                          {speaker.salutation && `${speaker.salutation} `}{speaker.name}
                        </h4>
                        {speaker.topic && (
                          <p className="text-sm text-blue-600 font-medium mt-1">{speaker.topic}</p>
                        )}
                        {speaker.bio && (
                          <p className="text-sm text-gray-600 mt-2">{speaker.bio}</p>
                        )}
                        {speaker.achievements && (
                          <div className="mt-2">
                            <span className="text-xs font-medium text-gray-700">Achievements:</span>
                            <p className="text-xs text-gray-600 mt-1">{speaker.achievements}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Event Flyer */}
          {event.eventFlyer && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Event Flyer</h3>
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {event.eventFlyer.toLowerCase().includes('.pdf') ? (
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                        <span className="text-red-600 font-bold text-xs">PDF</span>
                      </div>
                    ) : (
                      <img 
                        src={event.eventFlyer} 
                        alt="Event Flyer"
                        className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span class="text-blue-600 font-bold text-xs">IMG</span>
                            </div>
                          `;
                        }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Event Flyer</p>
                    <a 
                      href={event.eventFlyer} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      View/Download Flyer
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )
    }
  ];

  // Add hackathon-specific tabs
  if (isHackathon && event.hackathon) {
    tabs.push(
      {
        id: 'problems',
        label: 'Problem Statements',
        content: (
          <div className="space-y-4">
            {event.hackathon.problemStatements && event.hackathon.problemStatements.length > 0 ? (
              event.hackathon.problemStatements.map((problem, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-semibold text-lg">{problem.title}</h4>
                      <StatusBadge status={problem.difficulty} />
                    </div>
                    <p className="text-gray-600 mb-3">{problem.description}</p>
                    <div className="text-sm text-gray-500">Category: {problem.category}</div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No problem statements available yet.</p>
              </div>
            )}
          </div>
        )
      },
      {
        id: 'prizes',
        label: 'Prizes',
        content: (
          <div className="space-y-4">
            {event.hackathon.prizes && event.hackathon.prizes.length > 0 ? (
              event.hackathon.prizes.map((prize, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-2">
                      <Award className="h-5 w-5 mr-2 text-yellow-500" />
                      <h4 className="font-semibold">{prize.description}</h4>
                    </div>
                    <p className="text-lg font-bold text-blue-600">{prize.value}</p>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">Prize information will be announced soon.</p>
              </div>
            )}
          </div>
        )
      },
      {
        id: 'timeline',
        label: 'Timeline',
        content: (
          <div>
            {event.hackathon?.phases && event.hackathon.phases.length > 0 ? (
              <Timeline phases={event.hackathon.phases} />
            ) : (
              <div className="text-center py-12">
                <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No phases defined</h3>
                <p className="text-gray-600">Timeline will be available once phases are added</p>
              </div>
            )}
          </div>
        )
      },
      {
        id: 'rules',
        label: 'Rules & Requirements',
        content: (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Rules</h3>
              {event.hackathon.rules && event.hackathon.rules.length > 0 ? (
                <ul className="list-disc list-inside space-y-2">
                  {event.hackathon.rules.map((rule, index) => (
                    <li key={index} className="text-gray-700">{rule}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-600">Rules will be announced soon.</p>
              )}
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-3">Requirements</h3>
              {event.hackathon.requirements && event.hackathon.requirements.length > 0 ? (
                <ul className="list-disc list-inside space-y-2">
                  {event.hackathon.requirements.map((requirement, index) => (
                    <li key={index} className="text-gray-700">{requirement}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-600">Requirements will be announced soon.</p>
              )}
            </div>
          </div>
        )
      }
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <Link to="/events" className="flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Link>
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {isHackathon ? (
                <Trophy className="h-6 w-6 text-yellow-500" />
              ) : (
                <Calendar className="h-6 w-6 text-blue-600" />
              )}
              <h1 className="text-3xl font-bold text-gray-900">{event.title}</h1>
            </div>
            <div className="flex items-center space-x-4">
              <StatusBadge status={getEventStatus(event)} />
              <span className="text-gray-600 capitalize">{event.eventType.replace('_', ' ')}</span>
              {isHackathon && event.hackathon && (
                <span className="text-gray-600">
                  {event.hackathon.currentTeams}/{event.hackathon.maxTeams} teams
                </span>
              )}
            </div>
            
            {/* Team Status Message */}
            {isHackathon && event.hackathon && takesPart && (() => {
              const userTeam = getUserTeamForHackathon(event.hackathon._id);
              const userRole = getUserRoleInTeam(userTeam);
              
              if (userTeam && userTeam.status && userTeam.status.trim() !== '') {
                return (
                  <div className="mt-3">
                    {userRole === 'leader' ? (
                      <div className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md inline-block">
                        You are the leader and team is in {userTeam.status} state
                      </div>
                    ) : (
                      <div className="text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-md inline-block">
                        You are already a member of the team
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3">
            {/* Debug refresh button - only show in development */}

            {/* Show 3-button layout for non-signed-in users when registration is required */}
            {event.attendanceMode !== 'rsvp' && event.registrationRequired && isUserLoaded && !user && (
              <>
                {event.registrationType === 'optional_signup' && !(event.isPaidEvent && (event.eventFee || 0) > 0) ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Existing User button */}
                    <Link
                      to="/login"
                      className="inline-flex items-center justify-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                    >
                      Existing User
                    </Link>
                    {/* Guest User button */}
                    <Link
                      to={`/events/${event._id}/register`}
                      className="inline-flex items-center justify-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                    >
                      Guest User
                    </Link>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                  >
                    Sign In to Register
                  </Link>
                )}
              </>
            )}
            
            <AddToCalendar
              eventId={event._id}
              title={event.title}
              description={event.description}
              startTime={event.startTime}
              endTime={event.endTime}
              location={event.location}
              isVirtual={event.isVirtual}
              meetingLink={event.meetingLink}
            />

            {/* RSVP events take one tap instead of the full registration form */}
            {event.attendanceMode === 'rsvp' && isUserLoaded && user && (
              <div>
                <RsvpControl
                  eventId={event._id}
                  capacity={event.capacity}
                  attendingCount={event.currentRegistrations}
                />
                <CheckInPass eventId={event._id} />
              </div>
            )}

            {event.attendanceMode === 'rsvp' && isUserLoaded && !user && (
              <Link
                to="/login"
                className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-md hover:bg-primary-dark transition-colors text-sm font-medium"
              >
                Sign In to RSVP
              </Link>
            )}

            {/* Show registration buttons for signed-in users */}
            {event.attendanceMode !== 'rsvp' && event.registrationRequired && isUserLoaded && user && (
              // Only a hackathon with its own record has the team features that
              // the aspirant-only rule was written for. An ordinary event typed
              // as a hackathon is just an event, and mentors can register for it
              // like any other.
              // Everyone who may take part: aspirants and mentors, and
              // administrators so the participant's path can be walked from the
              // inside rather than only described. A hackathon is no different —
              // mentors were left out here while the server happily let them
              // create and join teams, so a registered mentor saw no team
              // options at all.
              ['aspirant', 'mentor', 'admin'].includes(user?.role || '')
            ) && (
              <>
                {(() => {
                  // For hackathons, check registration status and team membership
                  if (isHackathon && event.hackathon) {
                    const registrationStatus = getRegistrationStatus(event.hackathon._id);
                    const isRegistered = registrationStatus?.isRegistered || false;
                    const userTeam = getUserTeamForHackathon(event.hackathon._id);
                    const userRole = getUserRoleInTeam(userTeam);
                    
                    console.log('🎯 EventDetail Button rendering state:', {
                      isChecking: isChecking(event.hackathon._id),
                      isRegistered,
                      userRole: user?.role,
                      isUserLoaded,
                      registrationStatus,
                      hasTeam: !!userTeam,
                      teamRole: userRole
                    });
                    
                    if (isChecking(event.hackathon._id) || teamsLoading) {
                      return (
                        <div className="bg-gray-200 text-gray-600 px-6 py-2 rounded-md">
                          Checking...
                        </div>
                      );
                    } else if (!isRegistered) {
                      return (
                        <Button onClick={handleRegister}>
                          Register Now
                        </Button>
                      );
                    } else if (userTeam && userTeam.status && userTeam.status.trim() !== '') {
                      // User is already in a team for this hackathon
                      return (
                        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                          <Link
                            to={`/hackathons/${event.hackathon._id}/teams/discover`}
                            className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                          >
                            Browse Teams
                          </Link>
                          <Link
                            to="/teams/my-teams"
                            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                          >
                            My Teams
                          </Link>
                          <Button
                            onClick={() => {
                              setShowAvailableAspirants(true);
                              fetchAvailableAspirants();
                            }}
                            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                          >
                            Available Aspirants
                          </Button>
                          <Button
                            variant="outline"
                            className="text-red-600 hover:text-red-700 border-red-600 hover:border-red-700 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                            onClick={() => setShowUnregisterDialog(true)}
                          >
                            Unregister
                          </Button>
                        </div>
                      );
                    } else {
                      return (
                        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                          {registrationStatus?.registration?.paymentId && (
                            <button
                              onClick={() => handleDownloadReceipt(registrationStatus.registration.paymentId)}
                              className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors flex items-center text-sm font-medium whitespace-nowrap min-w-[120px]"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download Receipt
                            </button>
                          )}
                          <Link
                            to={`/hackathons/${event.hackathon._id}/teams/discover`}
                            className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                          >
                            Browse Teams
                          </Link>
                          <Link
                            to={`/teams/create?hackathon=${event.hackathon._id}`}
                            className="bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                          >
                            Create Team
                          </Link>
                          {/* Join Team button hidden as requested */}
                          {/* <Link
                            to={`/teams/join?hackathon=${event.hackathon._id}`}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                          >
                            Join Team
                          </Link> */}
                          <Link
                            to="/teams/my-teams"
                            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                          >
                            My Teams
                          </Link>
                          <Button
                            onClick={() => {
                              setShowAvailableAspirants(true);
                              fetchAvailableAspirants();
                            }}
                            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                          >
                            Available Aspirants
                          </Button>
                          <Button
                            variant="outline"
                            className="text-red-600 hover:text-red-700 border-red-600 hover:border-red-700 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap w-full sm:w-[160px] text-center h-12 flex items-center justify-center"
                            onClick={() => setShowUnregisterDialog(true)}
                          >
                            Unregister
                          </Button>
                        </div>
                      );
                    }
                  } else {
                    // For regular events, check registration status
                    const registrationStatus = getRegistrationStatus(event._id);
                    const isRegistered = registrationStatus?.isRegistered || false;
                    
                    console.log('🎯 EventDetail Regular Event Button rendering state:', {
                      isChecking: isChecking(event._id),
                      isRegistered,
                      userRole: user?.role,
                      isUserLoaded,
                      registrationStatus
                    });
                    
                    if (isChecking(event._id)) {
                      return (
                        <div className="bg-gray-200 text-gray-600 px-6 py-2 rounded-md">
                          Checking...
                        </div>
                      );
                    } else if (!isRegistered) {
                      return (
                        <Button onClick={handleRegister} className="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-3 rounded-md transition-all duration-300 shadow-lg hover:shadow-xl text-sm whitespace-nowrap min-w-[120px]">
                          Register Now
                        </Button>
                      );
                    } else {
                      // Check if registration requires approval and is still pending
                      const registrationStatus = getRegistrationStatus(id!);
                      const isPendingApproval = registrationStatus?.requiresApproval && registrationStatus?.status === 'registered';
                      
                      return (
                        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                          {registrationStatus?.registration?.paymentId && (
                            <button
                              onClick={() => handleDownloadReceipt(registrationStatus.registration.paymentId)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download Receipt
                            </button>
                          )}
                          <div className={`px-4 py-2 rounded-md ${
                            isPendingApproval 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {isPendingApproval ? '⏳ Pending Approval' : '✓ Registered'}
                          </div>
                          <Button
                            variant="outline"
                            className="text-red-600 hover:text-red-700 border-red-600 hover:border-red-700 px-4 py-2"
                            onClick={() => setShowUnregisterDialog(true)}
                          >
                            Unregister
                          </Button>
                        </div>
                      );
                    }
                  }
                })()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex w-full overflow-x-auto flex-wrap">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="flex-shrink-0">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>

      {/* Unregister Dialog */}
      <UnregisterDialog
        isOpen={showUnregisterDialog}
        onClose={() => setShowUnregisterDialog(false)}
        onConfirm={handleUnregister}
        eventTitle={event?.title || ''}
        eventType={event?.eventType === 'hackathon' ? 'hackathon' : 'event'}
        eventFee={event?.eventFee || 0}
        isPaidEvent={event?.isPaidEvent || false}
        isInTeam={false} // TODO: Check if user is in team
        isTeamLeader={false} // TODO: Check if user is team leader
        loading={unregisterLoading}
      />

      {/* Available Aspirants Dialog */}
      <Dialog open={showAvailableAspirants} onOpenChange={setShowAvailableAspirants}>
        <DialogContent className="max-w-6xl max-h-[80vh] w-[95vw] max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>Available Aspirants for {event?.title}</DialogTitle>
            <DialogDescription>
              View all registered and confirmed aspirants who are not part of any team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex flex-col h-full">
            {/* Search Box */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2">
              <Input
                placeholder="Search by name, email, phone, location, or skills..."
                value={aspirantsSearchTerm}
                onChange={(e) => setAspirantsSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={fetchAvailableAspirants}
                disabled={aspirantsLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {aspirantsLoading ? <LoadingSpinner size="sm" /> : 'Refresh'}
              </Button>
            </div>

            {/* Count Display */}
            <div className="text-sm text-gray-600">
              Showing {filteredAspirants.length} of {availableAspirants.length} aspirants
              {aspirantsSearchTerm && (
                <span className="ml-2 text-blue-600">
                  (filtered by "{aspirantsSearchTerm}")
                </span>
              )}
            </div>

            {/* Aspirants List - Desktop Table View */}
            <div className="hidden lg:block border rounded-lg overflow-hidden flex-1">
              <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Skills</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Experience</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {aspirantsLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center">
                          <LoadingSpinner size="lg" />
                        </td>
                      </tr>
                    ) : filteredAspirants.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          {aspirantsSearchTerm ? 'No aspirants found matching your search' : 'No available aspirants found'}
                        </td>
                      </tr>
                    ) : (
                      filteredAspirants.map((aspirant, index) => (
                        <tr key={aspirant._id || index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {aspirant.firstName} {aspirant.lastName}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v10a2 2 0 002 2z" />
                                </svg>
                                {aspirant.email}
                              </div>
                              <div className="flex items-center text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {aspirant.phone}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {aspirant.address || 'Not specified'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              {aspirant.primarySkills && aspirant.primarySkills.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {aspirant.primarySkills.map((skill: string, skillIndex: number) => (
                                    <span key={skillIndex} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {aspirant.secondarySkills && aspirant.secondarySkills.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {aspirant.secondarySkills.map((skill: string, skillIndex: number) => (
                                    <span key={skillIndex} className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center text-sm text-gray-600">
                              {String(getAspirantExperience(aspirant)).toLowerCase() === 'student' ? (
                                <span className="mr-2 font-medium text-blue-600">Student</span>
                              ) : (
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                </svg>
                              )}
                              {getAspirantExperience(aspirant)}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Aspirants List - Mobile Card View */}
            <div className="lg:hidden space-y-4 flex-1 overflow-y-auto max-h-[50vh]">
              {aspirantsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : filteredAspirants.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {aspirantsSearchTerm ? 'No aspirants found matching your search' : 'No available aspirants found'}
                </div>
              ) : (
                filteredAspirants.map((aspirant, index) => (
                  <Card key={aspirant._id || index} className="p-4">
                    <div className="space-y-3">
                      {/* Name */}
                      <div>
                        <h3 className="font-medium text-gray-900 text-lg">
                          {aspirant.firstName} {aspirant.lastName}
                        </h3>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="break-all">{aspirant.email}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>{aspirant.phone}</span>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="break-words">{aspirant.address || 'Not specified'}</span>
                      </div>

                      {/* Experience */}
                      <div className="flex items-center text-sm text-gray-600">
                        {String(getAspirantExperience(aspirant)).toLowerCase() === 'student' ? (
                          <span className="font-medium text-blue-600">Student</span>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            </svg>
                            <span>{getAspirantExperience(aspirant)}</span>
                          </>
                        )}
                      </div>

                      {/* Skills */}
                      <div className="space-y-2">
                        {aspirant.primarySkills && aspirant.primarySkills.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-gray-700 mb-1">Primary Skills:</h4>
                            <div className="flex flex-wrap gap-1">
                              {aspirant.primarySkills.map((skill: string, skillIndex: number) => (
                                <span key={skillIndex} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {aspirant.secondarySkills && aspirant.secondarySkills.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-gray-700 mb-1">Secondary Skills:</h4>
                            <div className="flex flex-wrap gap-1">
                              {aspirant.secondarySkills.map((skill: string, skillIndex: number) => (
                                <span key={skillIndex} className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowAvailableAspirants(false)}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetail; 